import { Address } from 'viem'

import { areAddressesEqual, getAddressKey, SupportedChainId } from '@cowprotocol/cow-sdk'
import { UiOrderType } from '@cowprotocol/types'

import { Order } from 'legacy/state/orders/actions'
import { _concatOrdersState } from 'legacy/state/orders/hooks'
import {
  ORDER_LIST_KEYS,
  OrdersState,
  OrdersStateNetwork,
  getDefaultNetworkState,
  PartialOrdersMap,
} from 'legacy/state/orders/reducer'
import { deserializeOrder } from 'legacy/state/orders/utils/deserializeOrder'

import { getUiOrderType } from 'utils/orderUtils/getUiOrderType'

export interface GetReduxOrdersByOrderTypeParams {
  account: Address
  reduxOrdersStateInCurrentChain: OrdersStateNetwork
  uiOrderType: UiOrderType
}

export interface ReduxOrdersByOrderTypeData {
  reduxOrders: Order[]
  ordersTokensSet: Set<string>
}

/**
 * Maps Redux per-chain order state into deserialized `Order[]` for a single `UiOrderType`.
 */
export function getReduxOrdersByOrderTypeFromNetworkState({
  account,
  reduxOrdersStateInCurrentChain,
  uiOrderType,
}: GetReduxOrdersByOrderTypeParams): ReduxOrdersByOrderTypeData {
  const reduxOrders: Order[] = []
  const ordersTokensSet = new Set<string>()

  _concatOrdersState(reduxOrdersStateInCurrentChain, ORDER_LIST_KEYS).forEach((order) => {
    if (!order) return

    const doesBelongToAccount = areAddressesEqual(order.order.owner, account)
    const orderUiOrderType = getUiOrderType(order.order)
    const doesMatchClass = orderUiOrderType === uiOrderType

    if (!doesBelongToAccount || !doesMatchClass) return

    const mappedOrder = deserializeOrder(order)

    if (!mappedOrder || mappedOrder.isHidden) return

    reduxOrders.push(mappedOrder)
    ordersTokensSet.add(getAddressKey(mappedOrder.inputToken.address))
  })

  return { reduxOrders, ordersTokensSet }
}

/**
 * Maps Redux per-chain order state into deserialized `Order[]`.
 */
export function getReduxOrdersByStatusFromNetworkState(
  account: Address,
  reduxOrdersInCurrentChainAndStatus: PartialOrdersMap,
): Order[] {
  const reduxOrders: Order[] = []

  Object.values(reduxOrdersInCurrentChainAndStatus).forEach((order) => {
    if (!order) return

    const doesBelongToAccount = areAddressesEqual(order.order.owner, account)

    if (!doesBelongToAccount) return

    const mappedOrder = deserializeOrder(order)

    if (!mappedOrder || mappedOrder.isHidden) return

    reduxOrders.push(mappedOrder)
  })

  return reduxOrders
}

export function getReduxOrdersStateByChain(
  reduxOrdersState: OrdersState,
  chainId: SupportedChainId,
): OrdersStateNetwork {
  const reduxOrdersStateByChain = reduxOrdersState?.[chainId]
  return { ...getDefaultNetworkState(chainId), ...reduxOrdersStateByChain }
}
